import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { useTabelle, TabellenKopf, Blaetterleiste, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { audit } from '../api';
import { format } from 'date-fns';

const PAGE_SIZE = 25;

const COMMON_ACTIONS = [
  'user.login',
  'user.logout',
  'instance.create',
  'instance.start',
  'instance.stop',
  'instance.deploy',
  'instance.delete',
  'order.create',
  'order.provision',
  'server.create',
  'server.decommission',
  'release.approve',
  'release.rollout',
];

const ENTITY_TYPES = [
  'User', 'Customer', 'Product', 'ProductPlan', 'Order',
  'Instance', 'Server', 'DeploymentJob', 'Release',
];

export default function AuditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');

  /**
   * Objekttyp und Objekt-Id kommen aus der Adresszeile.
   *
   * Damit ist ein Verweis wie `/audit?entityType=Instance&entityId=…` von
   * überall aus möglich — und das Protokoll einer bestimmten Instanz ist ein
   * Klick statt einer Suche. Genau das braucht man, wenn ein Kunde fragt, wer
   * wann an seiner Instanz war.
   */
  const [params, setParams] = useSearchParams();
  const entityTypeFilter = params.get('entityType') ?? '';
  const entityIdFilter = params.get('entityId') ?? '';

  const setEntityTypeFilter = (wert: string) => {
    const n = new URLSearchParams(params);
    if (wert) n.set('entityType', wert); else n.delete('entityType');
    setParams(n, { replace: true });
    setPage(1);
  };
  const objektLoesen = () => {
    const n = new URLSearchParams(params);
    n.delete('entityId');
    setParams(n, { replace: true });
    setPage(1);
  };

  // `limit`, nicht `pageSize`. Das Backend liest `limit`; der alte Name wurde
  // stillschweigend verworfen, und das Log stand immer auf der Vorgabe 50 --
  // waehrend PAGE_SIZE hier 25 behauptete.
  const { data, isLoading } = useQuery({
    queryKey: ['audit', { page, limit: PAGE_SIZE, action: actionFilter,
                          entityType: entityTypeFilter, entityId: entityIdFilter }],
    queryFn: () =>
      audit.list({
        page,
        limit: PAGE_SIZE,
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        entityId: entityIdFilter || undefined,
      }),
  });

  /** Exportiert genau das, was gerade gefiltert ist — nicht alles. */
  const exportieren = async () => {
    const blob = await audit.exportCsv({
      action: actionFilter || undefined,
      entityType: entityTypeFilter || undefined,
      entityId: entityIdFilter || undefined,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const teil = entityIdFilter ? `-${entityIdFilter}` : '';
    a.download = `protokoll${teil}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rows = data?.data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'zeit', label: t('tables.timestamp'), wert: (l) => l.createdAt },
    { key: 'nutzer', label: t('tables.user'), wert: (l) => l.userEmail ?? '' },
    { key: 'aktion', label: t('tables.action'), wert: (l) => l.action },
    { key: 'objekttyp', label: t('tables.entityType'), wert: (l) => l.entityType ?? '' },
    { key: 'objekt', label: t('tables.entityId'), wert: (l) => l.entityId ?? '' },
    { key: 'ip', label: t('tables.ip'), wert: (l) => l.ipAddress ?? '' },
    { key: 'automatisch', label: t('tables.automated'), wert: (l) => (l.userEmail ? 0 : 1) },
  ];

  // Ohne eigene Seitenaufteilung: das Log wird serverseitig geblaettert, `rows`
  // ist bereits genau eine Seite. Wuerde useTabelle hier noch einmal schneiden,
  // stuenden zwei Aufteilungen uebereinander.
  const tabelle = useTabelle({
    speichern: 'audit',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'zeit', richtung: 'ab' },
    proSeiteStart: 0,
  });

  // Die Gesamtzahl steht in `pagination.total`. Vorher wurde `data.total`
  // gelesen -- das gibt es nicht, also null Seiten, also war "weiter" immer
  // ausgegraut und man kam nie ueber Seite 1 hinaus.
  const total = data?.pagination?.total ?? 0;
  const totalPages = Math.max(1, data?.pagination?.pages ?? 1);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder={t('filters.filterAction')}
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          list="action-suggestions"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-52"
        />
        <datalist id="action-suggestions">
          {COMMON_ACTIONS.map((a) => <option key={a} value={a} />)}
        </datalist>
        <select
          value={entityTypeFilter}
          onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allEntityTypes')}</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {/*
          Ist auf ein einzelnes Objekt eingeschränkt, steht das sichtbar da.
          Ein Filter, der nur in der Adresszeile lebt, ist einer, den man
          vergisst — und dann wundert man sich über eine kurze Liste.
        */}
        {entityIdFilter && (
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-2.5 py-1.5 text-sm text-brand-800">
            Objekt <code className="text-xs">{entityIdFilter}</code>
            <button
              onClick={objektLoesen}
              title="Einschränkung aufheben"
              className="text-brand-600 hover:text-brand-900"
            >
              <X size={13} />
            </button>
          </span>
        )}

        <button
          onClick={exportieren}
          disabled={total === 0}
          title={entityIdFilter
            ? 'Protokoll dieses Objekts als CSV — für den Nachweis gegenüber dem Kunden'
            : 'Das gefilterte Protokoll als CSV'}
          className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download size={14} /> CSV
        </button>

        <span className="ml-auto text-sm text-slate-500">{total} Einträge</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400 italic">{t('empty.audit')}</td>
              </tr>
            ) : (
              tabelle.sichtbar.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                    {format(new Date(log.createdAt), 'PP HH:mm:ss')}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {log.userEmail ?? log.userId?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                      {log.action}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{log.entityType ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400 truncate max-w-32">
                    {log.entityId?.slice(0, 12) ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{log.ipAddress ?? '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {log.automated ? (
                      <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Auto</span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste
        blaettern={{
          seite: page,
          seiten: totalPages,
          setSeite: setPage,
          von: total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
          bis: Math.min(page * PAGE_SIZE, total),
          gesamt: total,
          proSeite: PAGE_SIZE,
        }}
      />
    </div>
  );
}
